import { Injectable } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { IDeliveriesState } from './deliveries-state';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
    DELIVERY_INTEGRATIONS_FETCH,
    DELIVERY_INTEGRATION_CONNECT,
    DELIVERY_INTEGRATION_DISCONNECT,
    DELIVERY_ORDERS_FILTERS_FETCH,
    DELIVERY_ORDERS_POLLING_START,
    DELIVERY_ORDERS_POLLING_STOP,
    DELIVERY_ORDERS_SEARCH,
    DELIVERY_ORDERS_SEARCH_MORE,
    DELIVERY_ORDERS_TRACKING_FETCH,
    DELIVERY_ORDERS_TRACKING_GROUP_FETCH,
    DELIVERY_ORDERS_TRACKING_POLLING_START,
    DELIVERY_ORDERS_TRACKING_POLLING_STOP,
    DELIVERY_ORDER_CANCEL,
    DELIVERY_ORDER_CANCEL_START,
    DELIVERY_ORDER_CREATE,
    DELIVERY_ORDER_DISPATCH,
    DELIVERY_ORDER_HELP_FETCH,
    DELIVERY_ORDER_MANAGER_NOTE_ADD_START,
    DELIVERY_ORDER_QUOTE_FETCH,
    DELIVERY_ORDER_REASSIGN,
    DELIVERY_ORDER_REDISPATCH,
    DELIVERY_ORDER_REFUND_REQUEST,
    DELIVERY_ORDER_REFUND_REQUEST_START,
    DELIVERY_ORDER_UPDATE,
    DELIVERY_ORDER_UPDATE_ADDRESS_START,
    LIVEVIEW_ORDERS_SEARCH,
    ROUTER_NAVIGATION
} from '../action-types';
import { Observable, delay, filter, from, interval, map, mergeMap, of, switchMap, take, takeUntil, takeWhile, withLatestFrom } from 'rxjs';
import { getLocationPreference, getPreference } from '../preference/preference-selectors';
import { IAppRouterState } from '../router/router-state';
import { FetchLocationsAction } from '../locations/locations-actions';
import {
    CancelDeliveryOrderAction,
    ConnectDeliveryIntegrationAction,
    CreateDeliveryOrderAction,
    DisconnectDeliveryIntegrationAction,
    DispatchDeliveryOrderAction,
    FetchDeliveryIntegrationsAction,
    FetchDeliveryOrderConfigAction,
    FetchDeliveryOrderGroupTrackingAction,
    FetchDeliveryOrderHelpAction,
    FetchDeliveryOrderQuoteAction,
    FetchDeliveryOrderTrackingAction,
    FetchDeliveryOrdersFiltersAction,
    MergeDeliveryOrdersAction,
    ReassignDeliveryOrderAction,
    RedispatchDeliveryOrderAction,
    RefundRequestAction,
    ResetDeliveryOrdersAction,
    SearchDeliveryOrdersAction,
    SearchMoreDeliveryOrdersAction,
    SetDeliveryIntegrationsAction,
    SetDeliveryIntegrationsIsFetching,
    SetDeliveryOrderAction,
    SetDeliveryOrderGroupTrackingAction,
    SetDeliveryOrderQuotesAction,
    SetDeliveryOrdersAction,
    SetDeliveryOrdersFetchingAction,
    SetDeliveryOrdersFetchingMoreAction,
    SetDeliveryOrdersFilterByAction,
    SetDeliveryOrdersFiltersAction,
    SetDeliveryOrdersSummaryAction,
    SetIsFetchingDeliveryOrderQuotesAction,
    SetNewDeliveryOrderAction,
    SetViewedDeliveryIntegrationAction,
    SetViewedDeliveryOrderAction,
    StartAddManagerNoteAction,
    StartCancelDeliveryOrderAction,
    StartDeliveryOrdersPollingAction,
    StartDeliveryTrackingPollingAction,
    StartRefundRequestAction,
    StartUpdateDeliveryOrderAction,
    StopDeliveryOrdersPollingAction,
    StopDeliveryTrackingPollingAction,
    UnsetDeliveryOrderAction,
    UpdateDeliveryOrderAction
} from './deliveries-actions';
import { isDefined, isEmptyObj } from '@core/utils/object';
import {
    BILLING,
    DELIVERIES,
    DELIVERIES_LIST,
    DELIVERIES_LIST_ASSIGN,
    DELIVERIES_LIST_CREATE,
    DELIVERIES_LIST_CREATE_ASSIGN,
    DELIVERIES_LIST_EXPORT,
    DELIVERIES_LIST_ORDER,
    DELIVERIES_LIST_REASSIGN,
    DELIVERIES_LIST_REDISPATCH,
    DELIVERIES_LIST_REDISPATCH_ASSIGN,
    DELIVERIES_MAP,
    DELIVERIES_MAP_ORDER,
    DELIVERIES_MAP_REDISPATCH_ASSIGN,
    DELIVERIES_SETTINGS,
    DELIVERIES_SETTINGS_INTEGRATIONS,
    DELIVERIES_SETTINGS_INTEGRATIONS_VIEW,
    DELIVERIES_SETTINGS_USERS,
    DELIVERIES_SETTINGS_USERS_CREATE,
    DEVSUPPORT,
    DEVSUPPORT_DASHBOARD,
    ERROR,
    LIVEVIEW,
    LOCATIONS_LIST,
    NO_STATE
} from '@core/router/app-states';
import { DeliveryListPreference } from '@core/models/preference/delivery-list.preference';
import { getDeliveryOrderConfigs, getDeliveryOrderQuotesRequestFromNewOrder, getDeliveryOrdersFilterBy, getDeliveryOrdersList, getDeliveryOrdersMeta, getSearchQuery, getViewedDeliveryOrder } from './deliveries-selectors';
import { getCompany, getUser } from '../session/session-selectors';
import { SearchDeliveryOrdersRequest } from '@core/models/delivery/search-delivery-orders-request';
import { SessionPreference } from '@core/models/preference/session-preference';
import { User } from '@core/models/user/user';
import * as moment from 'moment';
import { Moment } from 'moment';
import { MOMENT_UNIT } from '@core/utils/moment';
import { FORMAT } from '@core/utils/formats';
import { deepmerge } from '@core/utils/merge';
import { SearchDataService } from '@core/data/api/search/search-data.service';
import { DELIVERY_ORDER_ENTITY } from '../entity-types';
import { DeliveryOrder } from '@core/models/delivery/delivery-order';
import { ossCatchError } from '@core/operators/catch-error.operator';
import { NormalizeResponse } from '@core/operators/normalize/normalize-response';
import { SetDeliveryListPreferenceAction } from '../preference/preference-actions';
import { DeliveryOrdersSummary } from '@core/models/delivery/summary/delivery-orders-summary';
import { ossNormalize } from '@core/operators/normalize/normalize.operator';
import { Company } from '@core/models/common/company';
import { DateFilterRangeShortcut } from '@core/models/filter/date-filter-range-shorcut';
import { RouterGoAction, RouterNavigationCancelAction } from '../router/router-actions';
import { DispatchDataService } from '@core/data/api/dispatch/dispatch-data.service';
import { DeliveryOrderGroupTracking } from '@core/models/delivery/tracking/delivery-order-group-tracking';
import { PageMeta } from '@core/models/common/page-meta';
import { getRouterState } from '../router/router-selectors';
import { environment } from '@environments/environment';
import { DeliveriesDataService } from '@core/data/api/delivery/deliveries-data.service';
import { schema } from 'normalizr';
import { Dto } from '@core/data/dto/dto';
import { DTO_CUSTOM, DTO_DEFAULT_CREATE } from '@core/data/dto/dto-types';
import { DeliveryOrderQuote } from '@core/models/delivery/quote/delivery-order-quote';
import { parseOrderMarkers } from '@core/utils/deliveries';
import { DeliveryOrderDriver } from '@core/models/delivery/driver/delivery-order-driver';
import { DeliveryOrderStatus } from '@core/models/delivery/delivery-order-status';
import { NoAction } from '../shared/actions/no-action';
import { DeliveryOrderReassignRequest } from '@core/models/delivery/delivery-order-reassign-request';
import { TranslateManyPipe } from '@shared/pipes/string/translate-many.pipe';
import { DialogService } from '@components/dialog/dialog.service';
import { ToastService } from '../toast/toast.service';
import { $log } from '@core/utils/logger';
import { DialogUpdateAddressComponent } from '@features/deliveries/details/dialog-update-address/dialog-update-address.component';
import { DialogDeliveryOrderRefundComponent } from '@features/deliveries/refund/dialog-delivery-order-refund.component';
import { Toast } from '@core/models/toast/toast';
import { ToastType } from '@core/models/toast/toast-type';
import { SetToastAction } from '../toast/toast.actions';
import { DialogManagerNoteComponent } from '@features/deliveries/manager-note/dialog-manager-note.component';
import { DeliveryIntegration } from '@core/models/delivery/integrations/delivery-integration';
import { LocationPreference } from '@core/models/preference/location-preference';
import { WindowRef } from '@vendor/window/window.component';
import { SetViewedLiveViewOrderAction } from '../live-view/live-view-actions';
import { DeliveryOrderCourier } from '@core/models/delivery/courier/delivery-order-courier';

@Injectable()
export class DeliveriesEffects {

    private nativeWindow: Window;

    constructor(private store$: Store<IDeliveriesState>,
                private searchDataService: SearchDataService,
                private dispatchDataService: DispatchDataService,
                private deliveriesDataService: DeliveriesDataService,
                private translateManyPipe: TranslateManyPipe,
                private dialogService: DialogService,
                private toastService: ToastService,
                private windowRef: WindowRef,
                private actions$: Actions) {
        this.nativeWindow = windowRef.nativeWindow;
    }

    searchOrders$ = createEffect(() => 
        this.actions$.pipe(
            ofType<SearchDeliveryOrdersAction>(DELIVERY_ORDERS_SEARCH),
            withLatestFrom(
                this.store$.select(getSearchQuery),
                this.store$.select(getDeliveryOrdersFilterBy),
                this.store$.select(getPreference),
                this.store$.select(getUser),
                this.store$.select(getCompany)
            ),
            switchMap(
                ([action, searchQuery, filterBy, preference, sessionUser, company]: [
                    SearchDeliveryOrdersAction,
                    SearchDeliveryOrdersRequest,
                    any,
                    SessionPreference,
                    User,
                    Company
                ]) => {

                    let originalRequest: SearchDeliveryOrdersRequest;
                    if (!action.payload || !action.payload.request) {
                        originalRequest = deepmerge({}, searchQuery);
                    } else {
                        originalRequest = deepmerge({}, action.payload.request);
                    }
                    
                    // Detect any change from today with special ranges
                    const today: Moment = moment().startOf(MOMENT_UNIT.DAYS);
                    const isStartSameAsToday: boolean = moment(
                        originalRequest.filters?.start,
                        FORMAT.DATETIME_LONG
                    ).isSame(today, MOMENT_UNIT.DAYS);

                    if (
                        !isStartSameAsToday &&
                        filterBy?.rangeShortcut !==
                        DateFilterRangeShortcut.CUSTOM &&
                        isDefined(filterBy?.rangeShortcut)
                    ) {
                        let updatedStart: string;
                        let updatedEnd: string;
                        switch (filterBy?.rangeShortcut) {
                            case DateFilterRangeShortcut.LAST7:
                                updatedStart = moment(today)
                                    .subtract(6, MOMENT_UNIT.DAYS)
                                    .startOf(MOMENT_UNIT.DAYS)
                                    .format(FORMAT.DATETIME_SERVER);

                                updatedEnd = moment(today)
                                    .endOf(MOMENT_UNIT.DAYS)
                                    .format(FORMAT.DATETIME_SERVER);
                                break;

                            case DateFilterRangeShortcut.LAST14:
                                updatedStart = moment(today)
                                    .subtract(13, MOMENT_UNIT.DAYS)
                                    .startOf(MOMENT_UNIT.DAYS)
                                    .format(FORMAT.DATETIME_SERVER);

                                updatedEnd = moment(today)
                                    .endOf(MOMENT_UNIT.DAYS)
                                    .format(FORMAT.DATETIME_SERVER);
                                break;

                            case DateFilterRangeShortcut.LAST28:
                                updatedStart = moment(today)
                                    .subtract(27, MOMENT_UNIT.DAYS)
                                    .startOf(MOMENT_UNIT.DAYS)
                                    .format(FORMAT.DATETIME_SERVER);

                                updatedEnd = moment(today)
                                    .endOf(MOMENT_UNIT.DAYS)
                                    .format(FORMAT.DATETIME_SERVER);
                                break;

                            case DateFilterRangeShortcut.TODAY:
                                updatedStart = moment(today)
                                    .startOf(MOMENT_UNIT.DAYS)
                                    .format(FORMAT.DATETIME_SERVER);

                                updatedEnd = moment(updatedStart)
                                    .endOf(MOMENT_UNIT.DAYS)
                                    .format(FORMAT.DATETIME_SERVER);
                                break;
                        }

                        originalRequest.filters.start = updatedStart;
                        originalRequest.filters.end = updatedEnd;

                        // update the filter state as well
                        this.store$.dispatch(
                            new SetDeliveryOrdersFilterByAction({
                                start: updatedStart,
                                end: updatedEnd,
                            })
                        );
                    }

                    // create a copy since we modify the start and end fields
                    const request: SearchDeliveryOrdersRequest = deepmerge(
                        {},
                        originalRequest
                    );
                    // convert start and end to utc before sending ideally should happen in dto
                    request.filters.start = moment(
                        request.filters.start,
                        FORMAT.DATETIME_LONG
                    )
                        .utc()
                        .format(FORMAT.DATETIME_LONG);
                    request.filters.end = moment(
                        request.filters.end,
                        FORMAT.DATETIME_LONG
                    )
                        .utc()
                        .format(FORMAT.DATETIME_LONG);

                    // Make sure refund status is removed from request when dealing with deliveries list
                    request.filters.refund_status = undefined;

                    return this.searchDataService
                        .searchDeliveryOrders(
                            request,
                            1,
                            (action.payload && action.payload.perPage) || null
                        )
                        .pipe(
                            takeUntil(this.actions$.pipe(ofType(LIVEVIEW_ORDERS_SEARCH))),
                            ossNormalize(DELIVERY_ORDER_ENTITY, DeliveryOrder, 'deliveries'),
                            mergeMap((normalized: NormalizeResponse) => {

                                const actions: Action[] = [
                                    new SetDeliveryOrdersAction(normalized),
                                    new SetDeliveryOrdersFetchingAction(false)
                                ];

                                if (!!preference?.company && ! action.payload?.ignoreRemotePreferencesUpdate) {
                                    actions.push(new SetDeliveryListPreferenceAction({
                                        userId: sessionUser.id,
                                        companyId: preference.company.id,
                                        orderList: <DeliveryListPreference>(
                                            originalRequest
                                        ),
                                    }));
                                }

                                if (normalized.data.summary) {
                                    actions.push(
                                        new SetDeliveryOrdersSummaryAction({
                                            summary: DeliveryOrdersSummary.import(
                                                normalized.data.summary
                                            ),
                                        })
                                    );
                                }

                                return actions;
                            }),
                            ossCatchError(([actions]) =>
                                from(
                                    actions.concat([
                                        new SetDeliveryOrdersFetchingAction(false),
                                    ])
                                )
                            )
                        );
                }
            )
        )
    );
    
    searchMoreOrders$ = createEffect(() => this.actions$.pipe(
        ofType<SearchMoreDeliveryOrdersAction>(DELIVERY_ORDERS_SEARCH_MORE),
        withLatestFrom(
            this.store$.select(getSearchQuery),
            this.store$.select(getDeliveryOrdersList)
        ),
        switchMap(
            ([action, searchQuery, orderList]: [
                SearchMoreDeliveryOrdersAction,
                SearchDeliveryOrdersRequest,
                any
            ]) => {
                let request$: Observable<Action> = of(new NoAction());

                if (orderList.meta && orderList.meta.has_next) {
                    // create a copy since we modify the start and end fields
                    const request: SearchDeliveryOrdersRequest = deepmerge(
                        {},
                        searchQuery
                    );
                    // convert start and end to utc before sending ideally should happen in dto
                    request.filters.start = moment(
                        request.filters.start,
                        FORMAT.DATETIME_LONG
                    )
                        .utc()
                        .format(FORMAT.DATETIME_LONG);
                    request.filters.end = moment(
                        request.filters.end,
                        FORMAT.DATETIME_LONG
                    )
                        .utc()
                        .format(FORMAT.DATETIME_LONG);

                    // Make sure refund status is removed from request when dealing with deliveries list
                    request.filters.refund_status = undefined;

                    request$ = this.searchDataService
                        .searchDeliveryOrders(request, orderList.meta.page + 1)
                        .pipe(
                            ossNormalize(DELIVERY_ORDER_ENTITY, DeliveryOrder, 'deliveries'),
                            mergeMap((normalized: NormalizeResponse) => {
                                let actions: Action[] = [
                                    new SetDeliveryOrdersFetchingMoreAction(false),
                                    new MergeDeliveryOrdersAction(normalized),
                                ];

                                return actions;
                            }),
                            ossCatchError(([actions]) =>
                                from(
                                    actions.concat([
                                        new SetDeliveryOrdersFetchingMoreAction(false),
                                    ])
                                )
                            )
                        );
                }

                return request$;
            }
        )
    ));

    fetchOrdersFilters$ = createEffect(() => {
        return this.actions$.pipe(
            ofType<FetchDeliveryOrdersFiltersAction>(DELIVERY_ORDERS_FILTERS_FETCH),
            switchMap((action: FetchDeliveryOrdersFiltersAction) => {

                return this.searchDataService.fetchDeliveryOrderFilters().pipe(
                    map((response: any) => {
                        return new SetDeliveryOrdersFiltersAction({
                            filters: response
                        })
                    }),
                    ossCatchError(([actions]) => from(actions))
                );
            })
        );
    });

    fetchOrderGroupTracking$ = createEffect(() =>
        this.actions$.pipe(
            ofType<FetchDeliveryOrderGroupTrackingAction>(DELIVERY_ORDERS_TRACKING_GROUP_FETCH),
            switchMap((action: FetchDeliveryOrderGroupTrackingAction) => {
                return this.dispatchDataService
                    .fetchDispatchGroupTracking(action.payload.locationId)
                    .pipe(
                        mergeMap((trackingResponse: any) => {
                            return [
                                new SetDeliveryOrderGroupTrackingAction({
                                    tracking: DeliveryOrderGroupTracking.import(
                                        trackingResponse
                                    ),
                                }),
                            ];
                        }),
                        ossCatchError(([errorActions, errorResponse]) => {
                            return from(
                                errorActions.concat([
                                    (action as any).error(errorResponse),
                                ])
                            );
                        })
                    );
            })
        )
    );

    startOrdersPolling$ = createEffect(() => 
    
        this.actions$.pipe(
            ofType<StartDeliveryOrdersPollingAction>(DELIVERY_ORDERS_POLLING_START),
            switchMap((action: StartDeliveryOrdersPollingAction) => {
                const takeUntil$ = this.actions$.pipe(
                    ofType<StopDeliveryOrdersPollingAction>(DELIVERY_ORDERS_POLLING_STOP)
                );

                return interval(environment.settings.poll_orders_interval).pipe(
                    withLatestFrom(
                        this.store$.select(getDeliveryOrdersMeta),
                        this.store$.select(getRouterState)
                    ),
                    filter(
                        ([_, meta, routerState]: [any, PageMeta, IAppRouterState]) =>
                            routerState.name !== DELIVERIES_LIST_ORDER &&
                            routerState.name !== DELIVERIES_LIST_REDISPATCH_ASSIGN &&
                            routerState.name !== DELIVERIES_MAP_REDISPATCH_ASSIGN &&
                            routerState.name.indexOf(DELIVERIES_SETTINGS) === -1
                    ),
                    takeUntil(takeUntil$),
                    map(
                        ([_, meta, routerState]: [
                            any,
                            PageMeta,
                            IAppRouterState
                        ]) => {
                            let perPage: number;
                            if (meta?.page) {
                                perPage =
                                    environment.settings.listPerPage * meta.page;
                            }
                            return new SearchDeliveryOrdersAction({
                                perPage,
                                ignoreRemotePreferencesUpdate: true
                            });
                        }
                    ),
                    ossCatchError(([actions]) => from(actions))
                );
            })
        )
    );

    createOrder$ = createEffect(() => {
        return this.actions$.pipe(
            ofType<CreateDeliveryOrderAction>(DELIVERY_ORDER_CREATE),
            switchMap((action: CreateDeliveryOrderAction) => {
                const { request } = action.payload;
                const dto = request.export();
    
                return this.deliveriesDataService.createDelivery(dto).pipe(
                    mergeMap((res: any) => {
                        let actions: Action[] = [];
                        if (res?.delivery) {
                            actions.push(
                                new SetDeliveryOrderAction({
                                    item: DeliveryOrder.import(res.delivery),
                                    schema: new schema.Entity(DELIVERY_ORDER_ENTITY),
                                })
                            );
                        }
                        actions = [
                            ...actions,
                            new SearchDeliveryOrdersAction(),
                            (action as any).success(),
                        ];
    
                        return actions;
                    }),
                    ossCatchError(([actions, error]) =>
                        from(actions.concat([(action as any).error(error)]))
                    )
                );
            })
        );
    });

    updateOrder$ = createEffect(() => this.actions$.pipe(
        ofType<UpdateDeliveryOrderAction>(DELIVERY_ORDER_UPDATE),
        switchMap((action: UpdateDeliveryOrderAction) => {
            const { orderId, request } = action.payload;
            const dto = request.export();

            return this.deliveriesDataService.updateDelivery(orderId, dto).pipe(
                mergeMap((res: any) => {
                    let actions: Action[] = [];
                    if (res?.delivery) {
                        actions.push(new FetchDeliveryOrderTrackingAction({ orderId }));
                    }
                    actions = [
                        ...actions,
                        (action as any).success(),
                    ];

                    return actions;
                }),
                ossCatchError(([actions, error]) =>
                    from(actions.concat([(action as any).error(error)]))
                )
            );
        })
    ));

    cancelOrder$ = createEffect(() => this.actions$.pipe(
        ofType<CancelDeliveryOrderAction>(DELIVERY_ORDER_CANCEL),
        switchMap((action: CancelDeliveryOrderAction) => {
            const { orderId, isRedispatch } = action.payload;

            let params: any = null;

            if (isRedispatch) {
                params = { reason: 'redispatch' };
            }

            return this.deliveriesDataService
                .cancelDelivery(orderId, params)
                .pipe(
                    mergeMap((res: any) => {
                        return [
                            new SearchDeliveryOrdersAction(),
                            (action as any).success(res),
                        ];
                    }),
                    ossCatchError(([actions]) => from(actions))
                );
        })
    ));

    dispatchOrder$ = createEffect(() => this.actions$.pipe(
        ofType<DispatchDeliveryOrderAction>(DELIVERY_ORDER_DISPATCH),
        withLatestFrom(this.store$.select(getViewedDeliveryOrder)),
        switchMap(([action, viewedOrder]: [DispatchDeliveryOrderAction, DeliveryOrder]) => {
            const { orderId, order } = action.payload;
            const request: Dto = order.export(DTO_DEFAULT_CREATE);

            return this.deliveriesDataService
                .dispatchDelivery(orderId, request)
                .pipe(
                    mergeMap((res: any) => {
                        return [
                            new SetDeliveryOrderAction({
                                item: DeliveryOrder.import(res.delivery),
                                schema: new schema.Entity(DELIVERY_ORDER_ENTITY),
                            }),
                            (action as any).success(res),
                        ];
                    }),
                    ossCatchError(([errorActions, errorResponse]) => {
                        return from(
                            errorActions.concat([
                                (action as any).error(errorResponse),
                            ])
                        );
                    })
                );
        })
    ));

    redispatchOrder$ = createEffect(() => this.actions$.pipe(
        ofType<RedispatchDeliveryOrderAction>(DELIVERY_ORDER_REDISPATCH),
        switchMap((action: RedispatchDeliveryOrderAction) => {
            const { orderId, order } = action.payload;
            const request: Dto = order.export(DTO_DEFAULT_CREATE);

            return this.deliveriesDataService
                .redispatchDelivery(orderId, request)
                .pipe(
                    mergeMap((res: any) => {
                        return [
                            new SetDeliveryOrderAction({
                                item: DeliveryOrder.import(res.delivery),
                                schema: new schema.Entity(DELIVERY_ORDER_ENTITY),
                            }),
                            new SetDeliveryOrderAction({
                                item: Object.assign(new DeliveryOrder(order), {
                                    status: DeliveryOrderStatus.CANCELLED,
                                }),
                                schema: new schema.Entity(DELIVERY_ORDER_ENTITY),
                            }),
                            (action as any).success(res.delivery),
                        ];
                    }),
                    ossCatchError(([errorActions, errorResponse]) => {
                        return from(
                            errorActions.concat([
                                (action as any).error(errorResponse),
                            ])
                        );
                    })
                );
        })
    ));

    reassignOrder$ = createEffect(() => this.actions$.pipe(
        ofType<ReassignDeliveryOrderAction>(DELIVERY_ORDER_REASSIGN),
        switchMap((action: ReassignDeliveryOrderAction) => {
            const { orderId, driverId, autoAssign, id } = action.payload;
            const request: DeliveryOrderReassignRequest = {
                driver_id: driverId,
                auto_assign: autoAssign,
            };

            return this.dispatchDataService
                .reassignOrder(orderId, request)
                .pipe(
                    mergeMap((res: any) => {
                        return [
                            new FetchDeliveryOrderTrackingAction({ orderId: id }),
                            (action as any).success(res),
                        ];
                    }),
                    ossCatchError(([errorActions, errorResponse]) => {
                        return from(
                            errorActions.concat([
                                (action as any).error(errorResponse),
                            ])
                        );
                    })
                );
        })
    ));

    fetchOrderQuote$ = createEffect(() => this.actions$.pipe(
            ofType<FetchDeliveryOrderQuoteAction>(DELIVERY_ORDER_QUOTE_FETCH),
            withLatestFrom(
                this.store$.select(getViewedDeliveryOrder),
                this.store$.select(getDeliveryOrderQuotesRequestFromNewOrder)
            ),

            switchMap(
                ([action, order, newOrder]: [
                    FetchDeliveryOrderQuoteAction,
                    DeliveryOrder,
                    any
                ]) => {
                    const useNewOrder: boolean =
                        action.payload?.useNewOrder || false;

                    const request: Dto = useNewOrder
                        ? newOrder
                        : order?.export(DTO_CUSTOM) || newOrder;

                    this.store$.dispatch(
                        new FetchDeliveryOrderConfigAction({
                            locationId: order?.location.id || newOrder?.location_id,
                        })
                    );

                    let orderRequestOverride: any = {};

                    if (
                        action.payload?.deliveryTimeUtc ||
                        action.payload?.pickupRequiredAt
                    ) {
                        orderRequestOverride.delivery_time_utc =
                            action.payload.deliveryTimeUtc;
                        orderRequestOverride.pickup_at_ready =
                            action.payload.pickupRequiredAt;
                    }

                    return this.deliveriesDataService
                        .fetchDeliveriesQuote(
                            Object.assign({}, request, orderRequestOverride)
                        )
                        .pipe(
                            mergeMap((res: any) => {
                                const orderId: string =
                                    order?.id || newOrder.order_number;
                                const quotes: DeliveryOrderQuote[] = (
                                    res.quotes || []
                                ).map((quote: Dto) => DeliveryOrderQuote.import(quote));

                                return [
                                    new SetDeliveryOrderQuotesAction({ orderId, quotes }),
                                    new SetIsFetchingDeliveryOrderQuotesAction({
                                        value: false,
                                    }),
                                    (action as any).success(res),
                                ];
                            }),
                            ossCatchError(([actions]) =>
                                from(
                                    actions.concat([
                                        new SetIsFetchingDeliveryOrderQuotesAction({
                                            value: false,
                                        }),
                                    ])
                                )
                            )
                        );
                }
            )
        )
    );

    fetchDeliveryOrderTracking$ = createEffect(() => this.actions$.pipe(
        ofType<FetchDeliveryOrderTrackingAction>(DELIVERY_ORDERS_TRACKING_FETCH),
        withLatestFrom(this.store$.select(getViewedDeliveryOrder)),
        switchMap(([action, order]: [FetchDeliveryOrderTrackingAction, DeliveryOrder]) => {
            return this.deliveriesDataService
                .fetchDeliveryTracking(action.payload.orderId)
                .pipe(
                    delay(action.payload?.delay || 0),
                    mergeMap((response: any) => {
                        return [
                            new SetDeliveryOrderAction({
                                item: Object.assign(
                                    DeliveryOrder.import(response.delivery),
                                    {
                                        markers: parseOrderMarkers(
                                            response.order
                                        ),
                                        driver: !!response.delivery?.courier
                                            ? DeliveryOrderDriver.import(
                                                  response.delivery?.courier
                                              )
                                            : null,
                                        courier: !!response.delivery?.courier
                                            ? DeliveryOrderCourier.import(
                                                response.delivery?.courier
                                            )
                                            : null
                                    }
                                ),
                                schema: new schema.Entity(DELIVERY_ORDER_ENTITY),
                            }),
                        ];
                    }),
                    ossCatchError(([errorActions, errorResponse]) =>
                        from(errorActions)
                    )
                );
        })
    ));

    fetchOrderHelp$ = createEffect(() => this.actions$.pipe(
        ofType<FetchDeliveryOrderHelpAction>(DELIVERY_ORDER_HELP_FETCH),
        switchMap((action: FetchDeliveryOrderHelpAction) => {
            const { orderId } = action.payload;

            return this.deliveriesDataService.fetchDeliveryHelp(orderId).pipe(
                switchMap((response: any) => {
                    return [(action as any).success(response)];
                }),
                ossCatchError(([errorActions, errorResponse]) => {
                    return from([
                        (action as any).error(errorResponse),
                    ]);
                })
            );
        })
    ));

    startDeliveryTrackingPolling$ = createEffect(() => this.actions$.pipe(
        ofType<StartDeliveryTrackingPollingAction>(
            DELIVERY_ORDERS_TRACKING_POLLING_START
        ),
        switchMap((action: StartDeliveryTrackingPollingAction) => {
            const takeUntil$ = this.actions$.pipe(
                ofType<StopDeliveryTrackingPollingAction>(
                    DELIVERY_ORDERS_TRACKING_POLLING_STOP
                )
            );

            return interval(environment.settings.poll_orders_interval).pipe(
                withLatestFrom(this.store$.select(getViewedDeliveryOrder)),
                takeWhile(
                    ([_, viewedOrder]: [any, DeliveryOrder]) =>
                        viewedOrder.status === DeliveryOrderStatus.PICKUP ||
                        viewedOrder.status === DeliveryOrderStatus.DROPOFF
                ),
                takeUntil(takeUntil$),
                map(([_, viewedOrder]: [any, DeliveryOrder]) => {
                    return new FetchDeliveryOrderTrackingAction({
                        orderId: viewedOrder.id,
                    });
                }),
                ossCatchError(([actions]) => from(actions))
            );
        })
    ));

    startCancelOrder$ = createEffect(() => this.actions$.pipe(
        ofType<StartCancelDeliveryOrderAction>(DELIVERY_ORDER_CANCEL_START),
        withLatestFrom(this.store$.select(getRouterState)),
        switchMap(
            ([action, routerState]: [
                StartCancelDeliveryOrderAction,
                IAppRouterState
            ]) => {
                const order: DeliveryOrder = action.payload.order;
                const isNewOrder: boolean = order?.status === DeliveryOrderStatus.NEW;
                const orderTotalQuantity: number = order.orderTotalQuantity;
                const confirmTitle: string = this.translateManyPipe.transform(
                    isNewOrder
                        ? 'Order.Delete.Confirm'
                        : 'Order.Cancel.Confirm',
                    orderTotalQuantity,
                    {
                        count: orderTotalQuantity,
                        order: order.orderReference,
                    }
                );
                const confirmDialog$: any = this.dialogService
                    .confirm({
                        title: confirmTitle,
                        content: isNewOrder
                            ? 'Order.Delete.Confirm.Desc'
                            : 'Order.Cancel.Confirm.Desc',
                        icon: 'trash-outline',
                        theme: 'error',
                        confirmText: 'Common.Yes',
                        confirmBtnModifierClass: 'c-btn--danger',
                        hideCancelButton: true
                    })
                    .afterClosed();

                // this.store$.dispatch returns back to the observable action since the effect has already finished
                // and dispatched NoAction() to finish the current flow. But we want to dispatch and let observable
                // action know after the dialog is opened and does some post processing
                return (confirmDialog$ as Observable<boolean>).pipe(
                    take(1),
                    map((isConfirmed: boolean): any => {
                        if (isConfirmed) {
                            this.deliveriesDataService
                                .cancelDelivery(order.id)
                                .subscribe(
                                    (response) => {
                                        this.toastService.showSuccess(
                                            order.status === DeliveryOrderStatus.NEW
                                                ? 'Order.Delete.Success'
                                                : 'Order.Cancel.Success'
                                        );

                                        if (
                                            routerState.fromState.name.indexOf(
                                                DELIVERIES_MAP
                                            ) > -1
                                        ) {
                                            this.store$.dispatch(
                                                new RouterGoAction({
                                                    name: DELIVERIES_MAP,
                                                    options: {
                                                        reload: true,
                                                    },
                                                })
                                            );
                                        } else if (
                                            routerState.fromState.name.indexOf(
                                                DELIVERIES_LIST
                                            ) > -1
                                        ) {
                                            this.store$.dispatch(
                                                new RouterGoAction({
                                                    name: DELIVERIES_LIST,
                                                })
                                            );
                                        }  else if (
                                            routerState.fromState.name.indexOf(LIVEVIEW) > -1
                                        ) {
                                            this.store$.dispatch(new SetViewedLiveViewOrderAction({ orderId: null }));
                                            this.store$.dispatch(
                                                new RouterGoAction({
                                                    name: LIVEVIEW,
                                                    options: {
                                                        reload: true
                                                    }
                                                })
                                            );
                                        }

                                        if (order.status === DeliveryOrderStatus.NEW) {
                                            this.store$.dispatch(
                                                new UnsetDeliveryOrderAction({
                                                    id: order.id,
                                                })
                                            );
                                        } else {
                                            this.store$.dispatch(
                                                new FetchDeliveryOrderTrackingAction({
                                                    orderId: order.id,
                                                })
                                            );
                                        }

                                        this.store$.dispatch(
                                            (action as any).success(response)
                                        );
                                    },
                                    (errorResponse) => {
                                        $log.error(
                                            'failed cancel order',
                                            errorResponse
                                        );
                                        let errorText: string =
                                            'Errors.Unknown';

                                        // if error is thrown from backend, the api response would contain error info structured
                                        // in this format
                                        if (
                                            errorResponse &&
                                            errorResponse.error &&
                                            errorResponse.error.error
                                        ) {
                                            errorText =
                                                errorResponse.error.error;
                                        }

                                        this.toastService.showError(errorText);

                                        this.store$.dispatch(
                                            (action as any).error(errorResponse)
                                        );
                                    }
                                );
                        } else {
                            // hitting on cancel simply hides the dialog
                            this.store$.dispatch(
                                (action as any).success({
                                    closed: true,
                                })
                            );
                        }

                        return new NoAction();
                    })
                );
            }
        )
    ));

    startUpdateOrder$ = createEffect(() => this.actions$.pipe(
        ofType<StartUpdateDeliveryOrderAction>(DELIVERY_ORDER_UPDATE_ADDRESS_START),
        switchMap((action: StartUpdateDeliveryOrderAction) => {
            const order: DeliveryOrder = action.payload.order;
            const confirmDialog$: any = this.dialogService
                .open(DialogUpdateAddressComponent, {
                    order,
                })
                .afterClosed();

            return (confirmDialog$ as Observable<boolean>).pipe(
                take(1),
                map((saved: boolean) => {
                    return (action as any).success(saved);
                })
            );
        })
    ));

    startRefundRequest$ = createEffect(() => this.actions$.pipe(
        ofType<StartRefundRequestAction>(DELIVERY_ORDER_REFUND_REQUEST_START),
        switchMap((action: StartRefundRequestAction) => {
            const order: DeliveryOrder = action.payload.order;
            const confirmDialog$: any = this.dialogService
                .open(DialogDeliveryOrderRefundComponent, {
                    order,
                })
                .afterClosed();

            return (confirmDialog$ as Observable<boolean>).pipe(
                take(1),
                map((saved: boolean) => {
                    return (action as any).success(saved);
                })
            );
        })
    ));

    refundRequest$ = createEffect(() => this.actions$.pipe(
        ofType<RefundRequestAction>(DELIVERY_ORDER_REFUND_REQUEST),
        withLatestFrom(this.store$.select(getViewedDeliveryOrder)),
        switchMap(([action, viewedDeliveryOrder]: [RefundRequestAction, DeliveryOrder]) => {
            const { order, request } = action.payload;
            const dto = request.export();

            return this.dialogService
                .confirm({
                    title: 'Order.Refund.Confirm.Title',
                    content:
                        order.status !== DeliveryOrderStatus.DELIVERED &&
                        order.status !== DeliveryOrderStatus.CANCELLED
                            ? 'Order.Refund.Confirm.Desc2'
                            : 'Order.Refund.Confirm.Desc',
                    icon: 'as-refund',
                    theme: 'success',
                    small: true,
                })
                .afterClosed()
                .pipe(
                    switchMap((confirm) => {
                        if (confirm) {
                            return this.deliveriesDataService
                                .requestRefund(order.id, dto)
                                .pipe(
                                    mergeMap((res) => {
                                        const actions = [
                                            (action as any).success({
                                                success: true,
                                            }),
                                            new SetToastAction(
                                                new Toast(
                                                    ToastType.SUCCESS,
                                                    'Order.Refund.Submitted'
                                                )
                                            ),
                                        ];

                                        if (res?.delivery) {

                                            actions.push(
                                                new SetDeliveryOrderAction({
                                                    item: Object.assign(DeliveryOrder.import(res.delivery), {
                                                        markers: viewedDeliveryOrder?.markers || []
                                                    }),
                                                    schema: new schema.Entity(
                                                        DELIVERY_ORDER_ENTITY
                                                    ),
                                                })
                                            );
                                        }
                                        
                                        return actions;
                                    }),
                                    ossCatchError(([actions, error]) =>
                                        from(
                                            actions.concat([
                                                (action as any).error(error),
                                            ])
                                        )
                                    )
                                );
                        } else {
                            return of(true).pipe(
                                map((_) => {
                                    return (action as any).success({
                                        cancelled: true,
                                    });
                                })
                            );
                        }
                    })
                );
        })
    ));

    startAddManagerNote$ = createEffect(() => this.actions$.pipe(
        ofType<StartAddManagerNoteAction>(DELIVERY_ORDER_MANAGER_NOTE_ADD_START),
        switchMap((action: StartAddManagerNoteAction) => {
            const order: DeliveryOrder = action.payload.order;
            const confirmDialog$: any = this.dialogService
                .open(DialogManagerNoteComponent, {
                    order,
                })
                .afterClosed();

            return (confirmDialog$ as Observable<boolean>).pipe(
                take(1),
                map((saved: boolean) => {
                    return (action as any).success(saved);
                })
            );
        })
    ));

    fetchDeliveryIntegrations$ = createEffect(() =>
        this.actions$.pipe(
            ofType<FetchDeliveryIntegrationsAction>(DELIVERY_INTEGRATIONS_FETCH),
            switchMap((action: FetchDeliveryIntegrationsAction) => {

                const locationId: string = action.payload.locationId;

                return this.deliveriesDataService.fetchDeliveryIntegrations(locationId)
                        .pipe(
                            mergeMap((response) => {

                                const integrations: { [provider: string]: DeliveryIntegration } = {};
                                
                                (response?.results || []).forEach(integration => {
                                    integrations[integration.provider] = DeliveryIntegration.import(integration)
                                });

                                return [
                                    new SetDeliveryIntegrationsAction({ integrations }),
                                    new SetDeliveryIntegrationsIsFetching({ value: false })
                                ];
                            }),
                            ossCatchError(([actions]) =>
                                from(
                                    actions.concat([
                                        new SetDeliveryIntegrationsIsFetching({ value: false })
                                    ])
                                )
                            )
                        )
            })
        )
    );

    connectDeliveryIntegration$ = createEffect(() => 
        this.actions$.pipe(
            ofType<ConnectDeliveryIntegrationAction>(DELIVERY_INTEGRATION_CONNECT),
            withLatestFrom(
                this.store$.select(getLocationPreference)
            ),
            switchMap(([action, locationPreference]: [ConnectDeliveryIntegrationAction, LocationPreference]) => {

                return this.deliveriesDataService.connectDeliveryIntegration(locationPreference.id, action.payload.provider)
                    .pipe(
                        mergeMap((response) => {

                            this.nativeWindow.location.href = response.url;

                            return [
                                (action as any).success()
                            ]
                        }),
                        ossCatchError(([errorActions, errorResponse]) => from(errorActions.concat([
                            (action as any).error(errorResponse)
                        ])))
                    )
            })
        )
    );

    disconnectDeliveryIntegration$ = createEffect(() => 
        this.actions$.pipe(
            ofType<DisconnectDeliveryIntegrationAction>(DELIVERY_INTEGRATION_DISCONNECT),
            withLatestFrom(
                this.store$.select(getLocationPreference)
            ),
            switchMap(([action, locationPreference]: [DisconnectDeliveryIntegrationAction, LocationPreference]) => {

                return this.deliveriesDataService.disconnectDeliveryIntegration(locationPreference.id, action.payload.id)
                    .pipe(
                        mergeMap((response) => {
                            return [
                                new FetchDeliveryIntegrationsAction({
                                    locationId: locationPreference.id
                                }),
                                (action as any).success()
                            ]
                        }),
                        ossCatchError(([errorActions, errorResponse]) => from(errorActions.concat([
                            (action as any).error(errorResponse)
                        ])))
                    )
            })
        )
    );

    routerDeliveryOrders$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ROUTER_NAVIGATION),
            withLatestFrom(
                this.store$.select(getPreference),
                this.store$.select(getLocationPreference),
                this.store$.select(getDeliveryOrderConfigs)
            ),
            mergeMap(([action, userPreference, locationPreference, deliveryConfigs]) => {
                const routerState: IAppRouterState = (action as any).payload
                .routerState as IAppRouterState;
                const { name: stateName, fromState, params } = routerState;
                const actions: Action[] = [];

                if (stateName === DELIVERIES_LIST) {
                    let savedRequest: DeliveryListPreference =
                    (userPreference?.company as any)?.orderList;

                    if (savedRequest && !isEmptyObj(savedRequest)) {
                        if (savedRequest.filters) {
                            actions.push(
                                new SetDeliveryOrdersFilterByAction(savedRequest.filters)
                            );
                        }
                    }

                    if (fromState.name.indexOf(DELIVERIES_LIST) === -1 &&
                        fromState.name.indexOf(DEVSUPPORT_DASHBOARD) === -1) {
                        actions.push(new ResetDeliveryOrdersAction());
                    } 

                    actions.push(new SearchDeliveryOrdersAction());
                    actions.push(new FetchDeliveryOrdersFiltersAction());
                    actions.push(new StartDeliveryOrdersPollingAction());
                }

                else if (stateName === DELIVERIES_LIST_CREATE) {
                    if (fromState.name !== DELIVERIES_LIST) {

                        actions.push(new RouterNavigationCancelAction({ state: DELIVERIES_LIST_CREATE }))
                        actions.push(new RouterGoAction({ name: DELIVERIES_LIST }));
                        
                    } else {

                        actions.push(new SetNewDeliveryOrderAction({ order: null }));
                    }
                }

                else if (stateName === DELIVERIES_LIST_EXPORT) {
                    if (fromState.name !== DELIVERIES_LIST) {

                        actions.push(new RouterNavigationCancelAction({ state: DELIVERIES_LIST_EXPORT }))
                        actions.push(new RouterGoAction({ name: DELIVERIES_LIST }));
                        
                    }
                }

                else if (stateName === DELIVERIES_LIST_ORDER) {
                    if (
                        fromState.name !== DELIVERIES_LIST &&
                        fromState.name !== DELIVERIES_LIST_REDISPATCH &&
                        fromState.name !== DELIVERIES_LIST_REASSIGN &&
                        fromState.name !== DELIVERIES_LIST_ASSIGN &&
                        fromState.name.indexOf(DEVSUPPORT) === -1
                    ) {
                        actions.push(new RouterNavigationCancelAction({ state: stateName }))
                        actions.push(new RouterGoAction({ name: DELIVERIES_LIST }));
                    } else {
                        const orderId: string = routerState.params.orderId;

                        if (fromState.name.indexOf(DEVSUPPORT) > -1) {
                            this.store$.dispatch(new ResetDeliveryOrdersAction());
                            this.store$.dispatch(new SearchDeliveryOrdersAction());
                            actions.push(new FetchDeliveryOrdersFiltersAction());
                            actions.push(new FetchDeliveryOrderTrackingAction({ orderId, delay: 1000 }));
                        } else {
                            actions.push(new FetchDeliveryOrderTrackingAction({ orderId }));
                        }

                        actions.push(new SetViewedDeliveryOrderAction({ orderId }));
                        
                        actions.push(new StartDeliveryTrackingPollingAction());

                        
                    }
                }

                else if (stateName === DELIVERIES_LIST_REASSIGN) {
                    if (fromState.name === DELIVERIES_LIST_ORDER) {
                        actions.push(new FetchDeliveryOrderQuoteAction());
                    } else {
                        actions.push(new RouterGoAction({ name: DELIVERIES_LIST }));
                    }
                }

                else if (stateName === DELIVERIES_LIST_REDISPATCH) {
                    if (fromState.name === DELIVERIES_LIST_ORDER) {
                        actions.push(
                            new FetchDeliveryOrderConfigAction({
                                locationId: params?.order?.location?.id,
                            })
                        );
                    } else if (fromState.name !== DELIVERIES_LIST_REDISPATCH_ASSIGN) {
                        actions.push(new RouterGoAction({ name: DELIVERIES_LIST }));
                    }
                }

                else if (stateName === DELIVERIES_LIST_REDISPATCH_ASSIGN) {
                    if (fromState.name === DELIVERIES_LIST_REDISPATCH) {
                        actions.push(
                            new FetchDeliveryOrderQuoteAction({ useNewOrder: true })
                        );
                    } else {
                        actions.push(new RouterGoAction({ name: DELIVERIES_LIST }));
                    }
                }

                else if (stateName === DELIVERIES_LIST_CREATE_ASSIGN) {
                    if (fromState.name === DELIVERIES_LIST_CREATE) {
                        actions.push(new SetViewedDeliveryOrderAction({ orderId: null }));
                        actions.push(new FetchDeliveryOrderQuoteAction());
                    } else {
                        actions.push(new RouterGoAction({ name: DELIVERIES_LIST }));
                    }
                }
                
                else if (stateName === DELIVERIES_LIST_ASSIGN) {
                    if (fromState.name === DELIVERIES_LIST_ORDER) {
                        actions.push(new FetchDeliveryOrderQuoteAction());
                    } else {
                        actions.push(new RouterGoAction({ name: DELIVERIES_LIST }));
                    }
                }

                else if (stateName === DELIVERIES_MAP) {

                    if (fromState.name !== DELIVERIES_LIST &&
                        fromState.name !== DELIVERIES_MAP_ORDER) {

                        actions.push(new RouterNavigationCancelAction({ state: DELIVERIES_MAP }))
                        actions.push(new RouterGoAction({ name: DELIVERIES_LIST }));
                    }
                }

                else if (stateName === DELIVERIES_SETTINGS_INTEGRATIONS_VIEW) {

                    if(fromState.name === NO_STATE) {
                        actions.push(new RouterNavigationCancelAction({ state: DELIVERIES_SETTINGS_INTEGRATIONS_VIEW }))
                        actions.push(new RouterGoAction({ name: DELIVERIES_SETTINGS_INTEGRATIONS }));
                    
                    } else {
                        actions.push(new SetViewedDeliveryIntegrationAction({ provider: params.provider }));
                    }

                } else if (stateName === DELIVERIES_SETTINGS_USERS_CREATE) {

                    if(fromState.name === NO_STATE) {
                        actions.push(new RouterNavigationCancelAction({ state: DELIVERIES_SETTINGS_USERS_CREATE }))
                        actions.push(new RouterGoAction({ name: DELIVERIES_SETTINGS_USERS }));
                    
                    }

                } else if (stateName.includes(DELIVERIES_SETTINGS)) {
                    if (!fromState.name?.includes(DELIVERIES_SETTINGS)) {
                        actions.push(new FetchDeliveryOrdersFiltersAction());
                        // actions.push(new FetchLocationsAction());
                    }
                    
                    if (!!userPreference?.location && !deliveryConfigs[userPreference.location.id]) {
                        actions.push(new FetchDeliveryOrderConfigAction({ locationId: (userPreference.location as any)?.id }));
                    }

                    if (stateName === DELIVERIES_SETTINGS_INTEGRATIONS) {
                        if (!!locationPreference) {
                            actions.push(new FetchDeliveryIntegrationsAction({ locationId: locationPreference.id }))
                        }
                    }
                }

                if (
                    fromState.name.indexOf(DELIVERIES) >= 0 &&
                    stateName.indexOf(DELIVERIES) === -1
                ) {
                    // moving away from orders page
                    actions.push(new StopDeliveryOrdersPollingAction());
                } 

                if (
                    fromState.name === DELIVERIES_LIST_ORDER &&
                    stateName !== DELIVERIES_LIST_ORDER
                ) {
                    // Moving away from order details view
                    actions.push(new StopDeliveryTrackingPollingAction());
                }

                return actions;
            })
            
        )
    );
}
